import { useState } from 'react';
import { DigitalCheckoutFormContent } from 'sellDirect/components/closingDash/closing/DigitalCheckoutFormContent';
import { DigitalCheckoutFormPhoto } from 'sellDirect/declarations/DigitalCheckoutPhotos';
import { digitalCheckoutFormToMutationInput } from 'sellDirect/helpers/closingDash/closing';
import {
  IResetableFormProps,
  IResetableFormRenderProps,
  ResetableForm,
} from 'sellDirect/components/closingDash/ResetableForm';
import { useObservability } from 'shared/helpers/observability';
import { useSubmitDigitalCheckout } from 'sellDirect/graphql/useSubmitDigitalCheckout';
import { UnderContractOffer, ClosedOffer } from 'sellDirect/OfferContext';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { showToast } from '@opendoor/bricks/complex/Toast';

export interface DigitalCheckoutFormState {
  lockboxLocation?: string;
  lockboxLocationOther?: string;
  declaration?: string;
  phoneNumber?: string;
  photos?: Array<DigitalCheckoutFormPhoto>;
}

export interface DigitalCheckoutFormProps {
  offer: UnderContractOffer | ClosedOffer;
  closingTasks: ClosingTasks;
}

/**
 * Digital checkout form.
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const DigitalCheckoutForm: React.FC<DigitalCheckoutFormProps> = ({ offer }) => {
  const { trackEvent } = useObservability();

  const [formState, setFormState] = useState<DigitalCheckoutFormState>({
    phoneNumber: offer.lead.customerV2?.primaryPhoneNumber?.number?.value || '',
  });
  const { submitDigitalCheckout, loading } = useSubmitDigitalCheckout({
    onError: () =>
      showToast(
        'error',
        'Oops, something went wrong. Please try again in a few hours or contact your experience partner.',
        'cake-submit-digital-checkout-error-toast',
        { toastOptions: { position: 'bottom-left' } },
      ),
  });

  const onSubmit: IResetableFormProps['onSubmit'] = (resetableForm) => {
    const { formState } = resetableForm;
    setFormState(formState as DigitalCheckoutFormState);
    const digitalCheckoutInput = digitalCheckoutFormToMutationInput(formState);
    trackEvent('click', 'submit-digital-checkout');
    submitDigitalCheckout(digitalCheckoutInput);
  };

  return (
    <ResetableForm
      initialFormState={formState}
      onSubmit={onSubmit}
      render={(resetableForm: IResetableFormRenderProps) => (
        <DigitalCheckoutFormContent offer={offer} resetableForm={resetableForm} loading={loading} />
      )}
    />
  );
};
